/**
 * Thẻ "Ghi công chờ duyệt": các đơn đang chờ CHÍNH người xem xác nhận ghi công
 * (máy chủ đã lọc theo người duyệt của tiểu đội — 12a). Người duyệt có tên
 * trong đơn vẫn tự xác nhận được (CEO 01/11/2026). Khối "Đơn GNPM" (T4, CEO
 * 1a): ghi công đơn GNPM mặc định trọn cho người bán; chia ca / chốt hộ thì
 * tiểu đội trưởng bấm "Chia lại".
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { EmptyState } from "@/components/EmptyState";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { formatDate } from "@/lib/format";
import {
  type SalesGNPMOrder,
  type SalesPendingOrder,
  useConfirmCredits,
  useConfirmGNPM,
  useGNPMOrders,
  usePendingCredits,
  useResplitGNPM,
} from "../api";
import { CREDIT_KIND_LABEL, CREDIT_STATUS_LABEL, monthText, shareLabel } from "../labels";
import { PersonMark, SquadMark } from "./marks";
import { SalesPersonPicker } from "./SalesPersonPicker";

/** Một dòng người được ghi công: ảnh + tên · phần · ca đầu · logo tiểu đội. */
function CreditChip({ line }: { line: SalesPendingOrder["lines"][number] }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-full border border-border bg-surface py-1 pl-1 pr-3">
      <PersonMark staffId={line.staffId} name={line.staffName || line.staffId} className="font-medium" />
      <span>· {shareLabel(line.shareNum, line.shareDen)}</span>
      {line.firstShift ? <span className="text-muted">· ca đầu</span> : null}
      {line.squadName ? (
        <span className="text-muted">
          · <SquadMark squadId={line.squadId} name={line.squadName} size={18} />
        </span>
      ) : null}
    </span>
  );
}

/** Bộ chưa tra xong sổ khách thì chưa xác nhận được — khoá nút, nói lý do (CEO 4a, Luật 6). */
function confirmLabel(lookupStatus: string, busy: boolean): string {
  if (lookupStatus === "pending") return "Chờ máy tra sổ khách";
  if (lookupStatus === "failed") return "Chưa xác nhận được";
  return busy ? "Đang xác nhận…" : "Xác nhận";
}

function PendingRow({ order }: { order: SalesPendingOrder }) {
  const confirm = useConfirmCredits(order.orderId);
  const first = order.lines[0];
  if (!first) return null;
  const ownerLine = order.lines.find((l) => l.ownerName);
  const blocked = first.lookupStatus === "pending" || first.lookupStatus === "failed";
  return (
    <li className="space-y-2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">
            {order.orderCode || order.orderId} · {order.customer || "Khách chưa có tên"}
          </p>
          <p className="text-sm text-muted">
            {CREDIT_KIND_LABEL[first.kind] ?? first.kind}
            {first.purchaseNo ? ` · lần mua thứ ${first.purchaseNo}` : ""}
            {ownerLine ? (
              <>
                {" · sale chủ "}
                <PersonMark staffId={ownerLine.ownerStaffId} name={ownerLine.ownerName} size={20} title={false} />
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/orders/$orderId" params={{ orderId: order.orderId }} search={{ tab: "credit" }}>
              Mở đơn
            </Link>
          </Button>
          <Button
            disabled={confirm.isPending || blocked}
            onClick={() =>
              confirm.mutate(first.version, {
                onSuccess: () => toast.success(`Đã xác nhận ghi công đơn ${order.orderCode}`),
                onError: (e) => toast.error(e instanceof Error ? e.message : "Không xác nhận được ghi công"),
              })
            }
          >
            {confirmLabel(first.lookupStatus, confirm.isPending)}
          </Button>
        </div>
      </div>
      {first.lookupStatus === "pending" ? (
        <p role="status" className="text-sm text-muted">
          Đang tra sổ khách: {first.lookupNote}
        </p>
      ) : null}
      {first.lookupStatus === "failed" ? (
        <p role="alert" className="text-sm text-destructive">
          {first.lookupNote} —{" "}
          {first.kind === "returning"
            ? "mở đơn: người nhận cọc ghi công lại, hoặc bấm “Sửa lần mua / sale chủ” kèm lý do nếu sổ khách ghi sai."
            : "mở đơn để người nhận cọc ghi công lại."}
        </p>
      ) : null}
      {first.override ? (
        <p className="text-sm text-muted">
          {first.override.byName || "Ban giám đốc"} đã sửa lần mua / sale chủ: “{first.override.reason}”
        </p>
      ) : null}
      <ul className="flex flex-wrap gap-2 text-sm">
        {order.lines.map((l) => (
          <li key={l.id}>
            <CreditChip line={l} />
          </li>
        ))}
      </ul>
    </li>
  );
}

function GnpfPending() {
  const pending = usePendingCredits();
  if (pending.isError) return <QueryErrorNote error={pending.error} fallback="Không tải được ghi công chờ duyệt" />;
  if (pending.isLoading) return <p className="text-sm text-muted">Đang tải…</p>;
  const orders = pending.data ?? [];
  if (orders.length === 0) {
    return <EmptyState title="Không có ghi công nào chờ bạn xác nhận" />;
  }
  return (
    <ul className="divide-y divide-border rounded-md border border-border bg-surface">
      {orders.map((o) => (
        <PendingRow key={o.orderId} order={o} />
      ))}
    </ul>
  );
}

/** Các trường hợp chia một đơn GNPM: số người ⇒ chữ trên màn. */
const GNPM_CASES: { n: 1 | 2 | 3; label: string }[] = [
  { n: 1, label: "Một bạn — trọn đơn" },
  { n: 2, label: "Hai bạn — mỗi bạn một nửa (chốt ở ca thứ hai, hoặc chốt hộ)" },
  { n: 3, label: "Ba bạn — mỗi bạn một phần ba (chốt ở ca thứ ba hoặc ngày sau)" },
];

type ResplitProps = { order: SalesGNPMOrder; month: string; onDone: () => void };

/** Tiểu đội trưởng chia lại phần ghi công một đơn GNPM — người đầu là bạn trực ca đầu. */
function ResplitForm({ order, month, onDone }: ResplitProps) {
  const resplit = useResplitGNPM(month, order.orderId);
  const current = [...order.lines].sort((a, b) => Number(b.firstShift) - Number(a.firstShift));
  const startN = (current.length === 2 || current.length === 3 ? current.length : 1) as 1 | 2 | 3;
  const [n, setN] = useState<1 | 2 | 3>(startN);
  const [people, setPeople] = useState<(string | null)[]>(
    current.length > 0 ? current.map((l) => l.staffId) : [order.sellerStaffId || null],
  );
  const [note, setNote] = useState("");

  const pickCase = (count: 1 | 2 | 3) => {
    setN(count);
    setPeople((ps) => Array.from({ length: count }, (_, i) => ps[i] ?? null));
  };

  const submit = () => {
    if (people.some((p) => !p)) {
      toast.error("Chọn đủ người được ghi công");
      return;
    }
    const ids = people as string[];
    resplit.mutate(
      {
        people: ids.map((staffId) => ({ staffId, shareNum: 1, shareDen: n })),
        firstShiftStaffId: ids[0] ?? "",
        note: note.trim(),
        expectedVersion: order.version,
      },
      {
        onSuccess: () => {
          toast.success(`Đã chia lại đơn ${order.orderCode} — chờ xác nhận`);
          onDone();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Không chia lại được ghi công đơn GNPM"),
      },
    );
  };

  return (
    <div className="space-y-3 rounded-md border border-border bg-surface p-3">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">Trường hợp</span>
        <select
          aria-label="Trường hợp chia GNPM"
          value={n}
          onChange={(e) => pickCase(Number(e.target.value) as 1 | 2 | 3)}
          className="min-h-11 rounded-md border border-border bg-white px-2"
        >
          {GNPM_CASES.map((c) => (
            <option key={c.n} value={c.n}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      {people.map((p, i) => (
        <div key={i} className="flex flex-col gap-1 text-sm">
          <span className="text-muted">{i === 0 ? "Bạn trực ca đầu" : `Bạn thứ ${i + 1}`}</span>
          <SalesPersonPicker
            kind="sales"
            month={month}
            preferSquadIds={order.squadId ? [order.squadId] : undefined}
            value={p}
            onChange={(id) => setPeople((ps) => ps.map((x, j) => (j === i ? id : x)))}
            placeholder={i === 0 ? "Bạn trực ca đầu" : `Bạn thứ ${i + 1}`}
          />
        </div>
      ))}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">Ghi chú</span>
        <Input aria-label="Ghi chú chia lại" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button onClick={submit} disabled={resplit.isPending}>
          {resplit.isPending ? "Đang lưu…" : "Lưu chia lại"}
        </Button>
        <Button variant="outline" onClick={onDone}>
          Huỷ
        </Button>
      </div>
    </div>
  );
}

function GNPMRow({ order, month }: { order: SalesGNPMOrder; month: string }) {
  const confirm = useConfirmGNPM(month, order.orderId);
  const [editing, setEditing] = useState(false);
  const status = order.lines[0]?.status;
  return (
    <li className="space-y-2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">
            {order.orderCode || order.orderId} · lên ngày {formatDate(order.orderCreatedAt)}
          </p>
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted">
            Người bán:{" "}
            {order.sellerName ? <PersonMark staffId={order.sellerStaffId} name={order.sellerName} size={20} title={false} /> : "chưa rõ"}
            {order.squadName ? (
              <>
                · <SquadMark squadId={order.squadId} name={order.squadName} size={20} />
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {order.canResplit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              Chia lại
            </Button>
          ) : null}
          {order.canConfirm ? (
            <Button
              disabled={confirm.isPending}
              onClick={() =>
                confirm.mutate(order.version, {
                  onSuccess: () => toast.success(`Đã xác nhận chia lại đơn ${order.orderCode}`),
                  onError: (e) => toast.error(e instanceof Error ? e.message : "Không xác nhận được bộ chia lại"),
                })
              }
            >
              {confirm.isPending ? "Đang xác nhận…" : "Xác nhận"}
            </Button>
          ) : null}
        </div>
      </div>
      {order.locked ? <p className="text-sm text-muted">{order.locked}</p> : null}
      <ul className="flex flex-wrap gap-2 text-sm">
        {order.lines.length === 0 ? (
          <li>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface py-1 pl-1 pr-3">
              <PersonMark staffId={order.sellerStaffId} name={order.sellerName || "Người bán"} className="font-medium" />
              <span>· Trọn đơn</span>
            </span>
          </li>
        ) : (
          order.lines.map((l) => (
            <li key={l.id}>
              <CreditChip line={l} />
            </li>
          ))
        )}
        {status ? (
          <li>
            <Badge variant={status === "confirmed" ? "green" : "yellow"}>{CREDIT_STATUS_LABEL[status] ?? status}</Badge>
          </li>
        ) : null}
      </ul>
      {editing ? <ResplitForm order={order} month={month} onDone={() => setEditing(false)} /> : null}
    </li>
  );
}

/** Khối "Đơn GNPM" của tháng (T4, CEO 1a). */
function GNPMOrders({ month }: { month: string }) {
  const orders = useGNPMOrders(month);
  const title = `Đơn GNPM tháng ${monthText(month)}`;
  return (
    <section aria-labelledby="gnpm-orders-title" className="space-y-2">
      <h3 id="gnpm-orders-title" className="font-semibold">
        {title}
      </h3>
      <p className="text-sm text-muted">
        Ghi công đơn GNPM mặc định trọn cho người bán. Chốt ở ca sau hay có bạn chốt hộ thì bấm "Chia lại".
      </p>
      {orders.isError ? (
        <QueryErrorNote error={orders.error} fallback="Không tải được đơn GNPM của tháng" />
      ) : orders.isLoading ? (
        <p className="text-sm text-muted">Đang tải…</p>
      ) : (orders.data ?? []).length === 0 ? (
        <p className="text-sm text-muted">Không có đơn GNPM nào của tháng này cần bạn chia lại hay xác nhận.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-surface">
          {(orders.data ?? []).map((o) => (
            <GNPMRow key={o.orderId} order={o} month={month} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function PendingCreditsTab({ month }: { month: string }) {
  return (
    <div className="space-y-6">
      <GnpfPending />
      <GNPMOrders month={month} />
    </div>
  );
}
